import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calculator,
  ArrowRight,
  TrendingDown,
  TrendingUp,
  DollarSign,
  Clock,
  Users,
  Target,
  MapPin,
  Share2,
  Zap,
  Eye,
  Sparkles,
  HelpCircle
} from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import CookieBanner from "@/components/CookieBanner";

const WHATSAPP_URL =
  "https://wa.me/34672972514?text=Hola%2C%20he%20usado%20la%20calculadora%20ROI%20y%20quiero%20mi%20sesi%C3%B3n%20gratuita%20de%2030%20minutos";
const LOGO_URL = "/datyon-logo.png";
const FORMSPREE_URL = "https://formspree.io/f/myknvawy";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const formatEUR = (n) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
    useGrouping: "always"
  }).format(Math.max(0, Math.round(n)));

const SECTOR_BENCHMARKS = [
  { label: "Industria / Producción", range: "8 - 15 %", value: 12 },
  { label: "Distribución / Logística", range: "6 - 12 %", value: 9 },
  { label: "Comercio / Retail", range: "5 - 10 %", value: 7 },
  { label: "Servicios profesionales", range: "5 - 9 %", value: 7 },
  { label: "Agroalimentario", range: "7 - 14 %", value: 10 }
];

export default function CalculadoraROI() {
  // SEO
  useEffect(() => {
    document.title =
      "Calculadora ROI Power BI Murcia | Cuánto pierde tu empresa | Datyon";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Calculadora ROI Power BI gratuita. Descubre en 60 segundos cuánto dinero podría estar perdiendo tu empresa en Murcia por no tener dashboards ni control de datos."
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute(
        "href",
        "https://datyon.com/calculadora-roi-power-bi-murcia"
      );
    }

    // WebApplication JSON-LD para SEO local + rich data
    const ldScript = document.createElement("script");
    ldScript.type = "application/ld+json";
    ldScript.id = "roi-webapp-jsonld";
    ldScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "Calculadora ROI Power BI Murcia",
      "url": "https://datyon.com/calculadora-roi-power-bi-murcia",
      "description":
        "Calculadora ROI Power BI gratuita para empresas en Murcia. Estima en 60 segundos cuánto dinero está perdiendo tu empresa por falta de visibilidad de datos y cuánto podrías recuperar con dashboards de business intelligence.",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "browserRequirements": "Requires JavaScript. Compatible with modern browsers.",
      "inLanguage": "es-ES",
      "isAccessibleForFree": true,
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "EUR"
      },
      "areaServed": [
        {
          "@type": "AdministrativeArea",
          "name": "Murcia"
        },
        {
          "@type": "Country",
          "name": "España"
        }
      ],
      "provider": {
        "@type": "Organization",
        "name": "Datyon",
        "url": "https://datyon.com"
      }
    });
    document.head.appendChild(ldScript);

    window.scrollTo(0, 0);
    return () => {
      document.title = "Análisis de Datos en Murcia para Empresas | Datyon";
      if (metaDesc)
        metaDesc.setAttribute(
          "content",
          "Transforma los datos de tu empresa en decisiones rentables. Servicios de análisis de datos, business intelligence y modelos predictivos en Murcia."
        );
      if (canonical) canonical.setAttribute("href", "https://datyon.com/");
      const existing = document.getElementById("roi-webapp-jsonld");
      if (existing) existing.remove();
    };
  }, []);

  // Calculator state
  const [revenue, setRevenue] = useState("");
  const [employees, setEmployees] = useState("");
  const [inefficiency, setInefficiency] = useState(8); // %
  const [showResult, setShowResult] = useState(false);

  const result = useMemo(() => {
    const monthly = parseFloat(revenue) || 0;
    const emp = parseFloat(employees) || 0;
    const inef = parseFloat(inefficiency) || 0;

    const annualRevenue = monthly * 12;
    // Rango de pérdida: estimación conservadora (60%) y realista (100%)
    const lossLow = annualRevenue * (inef / 100) * 0.6;
    const lossHigh = annualRevenue * (inef / 100) * 1.0;
    // Recuperable con dashboards y BI: 20%-40% de la pérdida detectada (rango realista
    // observado en proyectos de business intelligence en empresas similares en Murcia).
    const detectedLoss = annualRevenue * (inef / 100);
    const recoverableLow = detectedLoss * 0.2;
    const recoverableHigh = detectedLoss * 0.4;
    // Horas perdidas en informes manuales: ~6h/mes/empleado en empresas sin BI
    const hoursLostMonth = emp * 6;
    const hoursLostYear = hoursLostMonth * 12;
    // Coste económico de esas horas (coste empresa fully-loaded estándar: 25 €/h)
    const HOURLY_COST_EUR = 25;
    const hoursCostYear = hoursLostYear * HOURLY_COST_EUR;
    return {
      annualRevenue,
      lossLow,
      lossHigh,
      recoverableLow,
      recoverableHigh,
      threeYearLow: lossLow * 3,
      threeYearHigh: lossHigh * 3,
      hoursLostMonth,
      hoursLostYear,
      hoursCostYear
    };
  }, [revenue, employees, inefficiency]);

  const canCalculate =
    parseFloat(revenue) > 0 &&
    parseFloat(employees) > 0 &&
    parseFloat(inefficiency) > 0;

  const handleCalculate = (e) => {
    e.preventDefault();
    if (!canCalculate) {
      toast.error("Rellena los 3 campos para calcular tu ROI");
      return;
    }
    setShowResult(true);
    setTimeout(() => {
      const el = document.getElementById("roi-result");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const handleShare = async () => {
    const text = `Según la calculadora ROI Power BI de Datyon, mi empresa podría estar perdiendo entre ${formatEUR(
      result.lossLow
    )} y ${formatEUR(result.lossHigh)} al año. Calcula el tuyo: https://datyon.com/calculadora-roi-power-bi-murcia`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Calculadora ROI Power BI Murcia", text });
        return;
      } catch {
        /* fallback */
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Resultado copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar el resultado");
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] relative overflow-x-hidden" data-testid="roi-page">
      <div className="noise-overlay" />
      <Toaster position="top-right" richColors />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="roi-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center pl-4" data-testid="roi-nav-logo">
              <img src={LOGO_URL} alt="DATYON - Calculadora ROI Power BI Murcia" className="h-20 w-auto" style={{ maxHeight: "80px" }} />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Inicio</Link>
              <Link to="/servicios" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Servicios</Link>
              <Link to="/sobre-mi" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Sobre mí</Link>
              <Link to="/blog" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Blog</Link>
              <Link to="/contacto" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Contacto</Link>
            </div>
          </div>
        </div>
      </nav>

      <main>
        {/* Hero */}
        <section className="relative section-padding pt-28 overflow-hidden" data-testid="roi-hero">
          <div className="spotlight-1 -top-40 -left-40" />
          <div className="spotlight-2 top-1/3 right-0" />
          <div className="max-w-5xl mx-auto w-full relative z-10">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="text-center">
              <motion.div variants={fadeInUp} className="flex items-center justify-center gap-2 mb-4">
                <Calculator className="w-4 h-4 text-indigo-400" />
                <p className="text-indigo-400 font-medium text-sm md:text-base">
                  Herramienta gratuita · 60 segundos
                </p>
              </motion.div>
              <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6" data-testid="roi-h1">
                Calculadora ROI Power BI{" "}
                <span className="text-gradient">para empresas</span>
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-slate-300 text-lg md:text-xl mb-4 max-w-3xl mx-auto">
                Descubre cuánto dinero podrías estar perdiendo sin saberlo.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-slate-400 text-base md:text-lg mb-2 max-w-3xl mx-auto leading-relaxed">
                La mayoría de empresas en Murcia no tienen visibilidad real de sus datos: deciden por
                intuición, descubren los problemas semanas tarde y arrastran fugas de margen que nadie está
                mirando. Esta calculadora te da, en menos de un minuto, una estimación honesta de cuánto
                te puede estar costando esa falta de control.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Calculator */}
        <section className="relative section-padding pt-4" data-testid="roi-calculator">
          <div className="max-w-3xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
              <Card className="bg-slate-900/80 border-slate-800 backdrop-blur" data-testid="roi-form-card">
                <CardContent className="p-6 md:p-10">
                  <form onSubmit={handleCalculate} className="space-y-6">
                    {/* Facturación */}
                    <div>
                      <label htmlFor="revenue" className="block text-white font-medium mb-2 flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-indigo-400" />
                        Facturación mensual de tu empresa (€)
                      </label>
                      <Input
                        id="revenue"
                        type="number"
                        inputMode="numeric"
                        min="0"
                        step="1000"
                        placeholder="Ej. 80000"
                        value={revenue}
                        onChange={(e) => setRevenue(e.target.value)}
                        className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 h-12 text-base"
                        data-testid="roi-input-revenue"
                      />
                      <p className="text-slate-500 text-xs mt-1">Estimación bruta media. Solo se usa para el cálculo.</p>
                    </div>

                    {/* Empleados */}
                    <div>
                      <label htmlFor="employees" className="block text-white font-medium mb-2 flex items-center gap-2">
                        <Users className="w-4 h-4 text-indigo-400" />
                        Número de empleados
                      </label>
                      <Input
                        id="employees"
                        type="number"
                        inputMode="numeric"
                        min="1"
                        step="1"
                        placeholder="Ej. 12"
                        value={employees}
                        onChange={(e) => setEmployees(e.target.value)}
                        className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 h-12 text-base"
                        data-testid="roi-input-employees"
                      />
                      <p className="text-slate-500 text-xs mt-1">Lo uso para estimar horas perdidas en informes manuales.</p>
                    </div>

                    {/* Ineficiencia */}
                    <div>
                      <label htmlFor="inefficiency" className="block text-white font-medium mb-2 flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <TrendingDown className="w-4 h-4 text-indigo-400" />
                          Ineficiencia / fuga de margen estimada
                        </span>
                        <span className="text-indigo-400 font-bold">{inefficiency}%</span>
                      </label>
                      <input
                        id="inefficiency"
                        type="range"
                        min="1"
                        max="20"
                        step="1"
                        value={inefficiency}
                        onChange={(e) => setInefficiency(parseInt(e.target.value, 10))}
                        className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                        data-testid="roi-input-inefficiency"
                      />
                      <div className="flex justify-between text-slate-500 text-xs mt-1">
                        <span>1%</span>
                        <span>10%</span>
                        <span>20%</span>
                      </div>
                      <div className="mt-3 grid sm:grid-cols-2 gap-2">
                        {SECTOR_BENCHMARKS.map((s) => (
                          <button
                            type="button"
                            key={s.label}
                            onClick={() => setInefficiency(s.value)}
                            className="text-left text-xs bg-slate-800/60 hover:bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 transition-colors"
                            data-testid={`roi-benchmark-${s.value}`}
                          >
                            <span className="text-slate-300 font-medium">{s.label}</span>
                            <span className="text-slate-500 block">{s.range}</span>
                          </button>
                        ))}
                      </div>
                      <p className="text-slate-500 text-xs mt-3">
                        Si no estás seguro, deja un 8 %. Es la media observada en PYMEs sin BI.
                      </p>
                    </div>

                    <Button
                      type="submit"
                      className="btn-glow w-full bg-indigo-600 hover:bg-indigo-700 text-white py-6 text-base rounded-full"
                      data-testid="roi-calculate-btn"
                    >
                      Calcular cuánto pierde mi empresa
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* Result */}
        <AnimatePresence>
          {showResult && (
            <motion.section
              id="roi-result"
              key="result"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="relative section-padding pt-4"
              data-testid="roi-result-section"
            >
              <div className="max-w-4xl mx-auto">
                {/* Headline result */}
                <Card className="bg-gradient-to-br from-indigo-600/20 to-slate-900/80 border-indigo-500/30 backdrop-blur" data-testid="roi-result-card">
                  <CardContent className="p-6 md:p-10">
                    <div className="flex items-center gap-2 text-indigo-400 text-sm font-medium mb-3">
                      <Sparkles className="w-4 h-4" />
                      Resultado de tu calculadora ROI Power BI Murcia
                    </div>
                    <p className="text-white text-xl md:text-2xl font-semibold leading-snug mb-2">
                      Según tus datos, tu empresa en Murcia podría estar perdiendo entre
                    </p>
                    <p className="text-3xl md:text-5xl font-extrabold text-gradient mb-3" data-testid="roi-loss-headline">
                      {formatEUR(result.lossLow)} y {formatEUR(result.lossHigh)} al año
                    </p>
                    <p className="text-slate-400 text-sm md:text-base mb-4">
                      Basado en datos habituales en empresas similares en Murcia. La cifra real solo se confirma
                      analizando tus datos en una sesión de diagnóstico.
                    </p>

                    {/* Recovery highlighted range */}
                    <div className="mt-5 p-4 md:p-5 rounded-xl bg-green-500/10 border border-green-500/30" data-testid="roi-recovery-highlight">
                      <div className="flex items-center gap-2 text-green-400 text-xs uppercase tracking-wider font-medium mb-1">
                        <TrendingUp className="w-4 h-4" />
                        Recuperación estimada con Power BI
                      </div>
                      <p className="text-white text-lg md:text-2xl font-bold">
                        Podrías recuperar entre {formatEUR(result.recoverableLow)} y {formatEUR(result.recoverableHigh)} al año
                      </p>
                      <p className="text-slate-400 text-xs md:text-sm mt-1">
                        Rango del 20 % al 40 % de la pérdida detectada, observado en proyectos de business intelligence en empresas de Murcia y alrededores.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Breakdown */}
                <div className="grid md:grid-cols-3 gap-4 mt-6">
                  <Card className="bg-slate-900 border-slate-800" data-testid="roi-breakdown-monthly">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider mb-2">
                        <TrendingDown className="w-4 h-4 text-red-400" />
                        Pérdida mensual
                      </div>
                      <div className="text-white text-xl font-bold">
                        {formatEUR(result.lossLow / 12)} - {formatEUR(result.lossHigh / 12)}
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-900 border-slate-800" data-testid="roi-breakdown-annual">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider mb-2">
                        <DollarSign className="w-4 h-4 text-red-400" />
                        Pérdida anual
                      </div>
                      <div className="text-white text-xl font-bold">
                        {formatEUR(result.lossLow)} - {formatEUR(result.lossHigh)}
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-900 border-slate-800" data-testid="roi-breakdown-3y">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider mb-2">
                        <Clock className="w-4 h-4 text-amber-400" />
                        Acumulado a 3 años
                      </div>
                      <div className="text-white text-xl font-bold">
                        {formatEUR(result.threeYearLow)} - {formatEUR(result.threeYearHigh)}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Hidden cost: hours converted to money */}
                <Card className="bg-slate-900 border-slate-800 mt-6" data-testid="roi-hours-card">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-400 flex-shrink-0">
                      <Clock className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Coste oculto: tiempo en informes manuales</h3>
                      <p className="text-slate-400 text-sm leading-relaxed mb-2">
                        Con {employees} empleados, tu equipo dedica aproximadamente{" "}
                        <strong className="text-white">{result.hoursLostMonth} horas/mes</strong> ({result.hoursLostYear} h/año)
                        a generar y revisar informes manuales que un Power BI bien montado actualizaría solo.
                      </p>
                      <p className="text-white text-base md:text-lg font-semibold" data-testid="roi-hours-cost">
                        Estás perdiendo aproximadamente {formatEUR(result.hoursCostYear)} al año en tareas manuales.
                      </p>
                      <p className="text-slate-500 text-xs mt-1">
                        Calculado a 25 €/h de coste empresa (salario + cargas + estructura), media estándar para PYMEs en Murcia.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Impact phrase */}
                <div className="mt-6 p-5 rounded-xl bg-red-500/5 border border-red-500/20 text-center" data-testid="roi-impact-phrase">
                  <p className="text-slate-200 text-base md:text-lg leading-snug">
                    La mayoría de empresas <span className="text-red-400 font-semibold">no detectan estas pérdidas</span> hasta que ya han perdido miles de euros.
                  </p>
                </div>

                {/* Single conversion block */}
                <Card className="bg-gradient-to-br from-indigo-600/30 to-purple-600/20 border-indigo-500/40 mt-6" data-testid="roi-cta-card">
                  <CardContent className="p-6 md:p-12 text-center">
                    <h3 className="text-2xl md:text-4xl font-extrabold text-white mb-4 leading-tight" data-testid="roi-cta-title">
                      ¿Cuánto de este dinero{" "}
                      <span className="text-gradient">podrías recuperar realmente</span>?
                    </h3>
                    <p className="text-slate-300 mb-3 max-w-2xl mx-auto text-base md:text-lg" data-testid="roi-cta-subtitle">
                      En 30 minutos analizo tu caso real y te digo exactamente qué está fallando en tu empresa.
                    </p>
                    <p className="text-slate-400 mb-8 max-w-2xl mx-auto text-sm md:text-base" data-testid="roi-cta-reinforce">
                      Esto no es una estimación genérica. Con datos reales, el impacto suele ser mayor.
                    </p>

                    {/* Primary CTA — dominant */}
                    <Button
                      asChild
                      className="btn-glow bg-indigo-600 hover:bg-indigo-700 text-white px-10 py-7 text-lg md:text-xl rounded-full font-semibold shadow-2xl shadow-indigo-600/40"
                      data-testid="roi-cta-contact"
                    >
                      <Link to="/contacto">
                        Analizar mi caso en 30 minutos
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Link>
                    </Button>

                    {/* Secondary — WhatsApp */}
                    <div className="mt-5 flex flex-col items-center gap-3">
                      <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-green-400 hover:text-green-300 text-sm font-medium transition-colors"
                        data-testid="roi-cta-whatsapp"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                        o si prefieres, hablemos por WhatsApp
                      </a>

                      {/* Tertiary — Share as discreet link */}
                      <button
                        type="button"
                        onClick={handleShare}
                        className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-300 text-xs font-medium transition-colors"
                        data-testid="roi-share-btn"
                      >
                        <Share2 className="w-3.5 h-3.5" />
                        Compartir resultado
                      </button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </motion.section>
          )}
        </AnimatePresence>

        {/* How it's calculated */}
        <section className="relative section-padding bg-slate-900/50" data-testid="roi-how">
          <div className="max-w-4xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-10">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4" data-testid="roi-h2-how">
                  Cómo se calcula tu <span className="text-gradient">ROI Power BI</span>
                </h2>
                <p className="text-slate-400 max-w-2xl mx-auto">
                  Sin trampas ni cifras infladas. Esto es exactamente lo que estamos asumiendo.
                </p>
              </motion.div>
              <div className="space-y-4">
                {[
                  {
                    icon: <DollarSign className="w-5 h-5" />,
                    title: "Facturación anual",
                    desc: "Multiplicamos tu facturación mensual por 12 para tener una base anual sobre la que estimar."
                  },
                  {
                    icon: <TrendingDown className="w-5 h-5" />,
                    title: "Rango de pérdida",
                    desc: "Aplicamos el % de ineficiencia que has indicado, en una horquilla conservadora (60% del valor) a realista (100%). Así obtienes un mínimo y un máximo creíbles, no un dato único inflado."
                  },
                  {
                    icon: <TrendingUp className="w-5 h-5" />,
                    title: "Recuperable con dashboards y BI",
                    desc: "Una solución de business intelligence Murcia bien implementada suele recuperar entre el 20% y el 40% de la pérdida detectada en los primeros 12 meses, según experiencia de campo en empresas similares en Murcia."
                  },
                  {
                    icon: <Clock className="w-5 h-5" />,
                    title: "Horas perdidas en informes manuales",
                    desc: "Estimamos 6 horas/mes/empleado dedicadas a montar Excel y reportes que un Power BI actualiza solo. Lo traducimos a euros aplicando un coste empresa estándar de 25 €/hora (salario + cargas + estructura)."
                  }
                ].map((item, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <div className="glass-card rounded-xl p-5 flex items-start gap-4" data-testid={`roi-how-${i}`}>
                      <div className="w-10 h-10 rounded-lg bg-indigo-600/20 flex items-center justify-center text-indigo-400 flex-shrink-0">
                        {item.icon}
                      </div>
                      <div>
                        <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                        <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Beneficios */}
        <section className="relative section-padding overflow-hidden" data-testid="roi-benefits">
          <div className="spotlight-1 -top-20 right-0" />
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-10">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4" data-testid="roi-h2-benefits">
                  Qué consigue tu empresa con{" "}
                  <span className="text-gradient">análisis de datos y Power BI</span>
                </h2>
                <p className="text-slate-400 max-w-2xl mx-auto">
                  Las cuatro palancas que más rentabilizan una inversión en datos y dashboards.
                </p>
              </motion.div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: <Eye className="w-6 h-6" />, title: "Visibilidad real del negocio", desc: "Dejas de gestionar a ciegas. Ves ventas, costes y márgenes en tiempo real, sin esperar al cierre." },
                  { icon: <Target className="w-6 h-6" />, title: "Decisiones con datos", desc: "Las reuniones dejan de ser opiniones. Pasan a ser conversaciones con cifras comparables y verificables." },
                  { icon: <Zap className="w-6 h-6" />, title: "Tiempo recuperado", desc: "Tu equipo deja de fabricar Excel a mano. El tiempo que ganas se invierte en lo que sí mueve el negocio." },
                  { icon: <DollarSign className="w-6 h-6" />, title: "Más margen, menos fugas", desc: "Detectas a tiempo productos, clientes y procesos que están drenando rentabilidad y los corriges antes." }
                ].map((b, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="h-full bg-slate-900 border-slate-800 card-hover text-center" data-testid={`roi-benefit-${i}`}>
                      <CardContent className="p-6">
                        <div className="w-12 h-12 rounded-xl bg-indigo-600/20 flex items-center justify-center text-indigo-400 mb-4 mx-auto">
                          {b.icon}
                        </div>
                        <h3 className="font-semibold text-white mb-2">{b.title}</h3>
                        <p className="text-slate-400 text-sm leading-relaxed">{b.desc}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* SEO Content Block */}
        <section className="relative section-padding bg-slate-900/50" data-testid="roi-seo-content">
          <div className="max-w-4xl mx-auto">
            <div className="text-slate-400 text-base leading-relaxed space-y-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                Calculadora ROI Power BI Murcia: una herramienta para empresas que quieren dejar de perder dinero
              </h2>
              <p>
                Esta calculadora ROI Power BI Murcia está pensada para directivos y gerentes de PYMEs que sospechan
                que algo se les está escapando, pero no saben cuantificarlo. En 60 segundos te da una primera
                estimación basada en tu facturación, tu tamaño de equipo y un porcentaje de ineficiencia razonable
                para tu sector. No es magia ni adivinación: son tres cifras simples que te permiten poner un orden
                de magnitud al problema antes de hablar con nadie.
              </p>
              <p>
                Si trabajas en{" "}
                <Link to="/business-intelligence-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                  business intelligence Murcia
                </Link>
                {" "}o estás considerando un proyecto de{" "}
                <Link to="/analisis-datos-empresas" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                  análisis de datos para empresas
                </Link>
                , esta página te sirve como caso de negocio inicial. Puedes llevar el resultado a una reunión
                interna y plantear la pregunta más importante: ¿cuánto nos cuesta seguir sin saber?
              </p>
              <h3 className="text-xl font-bold text-white mt-8 mb-4">Por qué un consultor Power BI puede ayudarte</h3>
              <p>
                La estimación de la calculadora es un punto de partida. La cifra real solo se confirma analizando
                los datos concretos de tu empresa: facturación por cliente, márgenes por línea de producto, rotación
                de stock, costes ocultos en operaciones, eficiencia de campañas. Mi trabajo como{" "}
                <Link to="/consultor-power-bi-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                  consultor Power BI en Murcia
                </Link>{" "}
                consiste exactamente en eso: convertir tus datos dispersos en dashboards claros donde dirección
                ve, en directo, dónde está ganando y dónde está perdiendo dinero.
              </p>
              <p>
                Si la calculadora te ha hecho levantar una ceja, da el siguiente paso. La sesión de 30 minutos
                es gratuita, sin compromiso y muy concreta: te digo qué mirar primero, qué dashboards tendrían
                más impacto y si Power BI es la herramienta adecuada para tu caso. También puedes pasarte por mi
                página de{" "}
                <Link to="/consultor-datos-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                  consultor de datos en Murcia
                </Link>{" "}
                para ver el alcance completo del servicio.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="relative section-padding bg-gradient-to-b from-slate-900/80 to-[#020617]" data-testid="roi-cta-final">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
              <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl font-bold mb-4" data-testid="roi-h2-final">
                ¿Quieres saber dónde se está escapando el dinero{" "}
                <span className="text-gradient">de verdad</span>?
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-slate-400 mb-3 max-w-xl mx-auto text-base leading-relaxed">
                En una sesión de 30 minutos te digo exactamente dónde está perdiendo dinero tu empresa en Murcia
                y cómo un Power BI bien diseñado puede ayudarte a recuperarlo.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-indigo-400 font-medium mb-8 text-base">
                Diagnóstico gratuito · Sin compromiso · Respuesta en menos de 24 horas
              </motion.p>

              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild className="btn-glow bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-base rounded-full" data-testid="roi-cta-final-contact">
                  <Link to="/contacto">
                    Reservar mi sesión de 30 minutos
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-base rounded-full" data-testid="roi-cta-final-whatsapp">
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                    Hablar por WhatsApp
                  </a>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800" data-testid="roi-footer">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="DATYON" className="h-8 w-auto" />
              <div className="flex items-center gap-2 text-slate-400">
                <MapPin className="w-4 h-4 text-indigo-400" />
                <span className="text-sm">Murcia, España</span>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-slate-500 hover:text-white text-sm transition-colors">Inicio</Link>
              <Link to="/servicios" className="text-slate-500 hover:text-white text-sm transition-colors">Servicios</Link>
              <Link to="/blog" className="text-slate-500 hover:text-white text-sm transition-colors">Blog</Link>
              <Link to="/contacto" className="text-slate-500 hover:text-white text-sm transition-colors">Contacto</Link>
            </div>
          </div>
          <div className="flex flex-wrap justify-center md:justify-start gap-4 mb-6 pt-4 border-t border-slate-800">
            <Link to="/aviso-legal" className="text-slate-500 hover:text-white text-sm transition-colors">Aviso Legal</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-privacidad" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Privacidad</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-cookies" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Cookies</Link>
          </div>
          <p className="text-slate-500 text-sm text-center md:text-left">
            © {new Date().getFullYear()} DATYON - Antonio Oliver. Todos los derechos reservados.
          </p>
          <p className="text-slate-600 text-xs mt-3 max-w-3xl">
            Aviso: la calculadora ROI Power BI Murcia ofrece una estimación orientativa basada en los inputs introducidos.
            No constituye asesoramiento financiero ni una garantía de resultado. La cifra real solo puede confirmarse
            analizando los datos concretos de tu empresa.
          </p>
        </div>
      </footer>

      {/* WhatsApp Floating */}
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg shadow-green-500/30 transition-all duration-300 hover:scale-110 group"
        data-testid="roi-whatsapp-float"
        aria-label="Contactar por WhatsApp"
      >
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
      </a>

      <CookieBanner />
    </div>
  );
}
